import { Fragment, useCallback, useEffect, useState } from "react";

import { ApiError, api } from "../../shared/api/client";
import type { AdminUser, Permission, Role } from "../../shared/api/types";
import { formatDateTime, useI18n } from "../../shared/i18n";
import { Icon } from "../../shared/ui/icons";
import { AdminTabs } from "./AdminTabs";
import { Select } from "../../shared/ui/Select";
import { SkeletonRows } from "../../shared/ui/Skeleton";
import { UserEditor } from "./UserEditor";

export function UsersPage() {
  const { t, lang } = useI18n();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [implications, setImplications] = useState<Record<string, string[]>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // First load only. A refetch keeps the existing rows on screen: swapping a
  // populated table for placeholders after every save is worse than a briefly
  // stale row, and it makes the page flicker on every edit.
  const [firstLoad, setFirstLoad] = useState(true);

  const load = useCallback(async () => {
    try {
      const [u, r] = await Promise.all([
        api.get<{ users: AdminUser[] }>("/users"),
        api.get<{
          roles: Role[]; permissions: Permission[]; implications: Record<string, string[]>;
        }>("/roles"),
      ]);
      setUsers(u.users);
      setRoles(r.roles);
      setPermissions(r.permissions);
      setImplications(r.implications ?? {});
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("users.loadFailed"));
    } finally {
      // In a finally, not after the success path: a failed first load must show the
      // error, not shimmer forever.
      setFirstLoad(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // `save` and `setRole` lived here: a PUT /users/{id} fired straight from the table
  // row's role dropdown, with no button and no confirmation. It was the third write path
  // for one record and the only one with no Save, and because it sent the LIST row's
  // values it silently discarded whatever was open in the editor below. The role is now
  // changed in the editor, with everything else about the person, behind one Save.

  return (
    <div className="stack">
      <AdminTabs />
      {error && (
        <p className="banner error" role="alert">
          {error}
        </p>
      )}

      <div className="row-between">
        <h2 className="section-title">{t("users.title")}</h2>
        <button type="button" className="btn primary" onClick={() => setCreating(true)}>
          <Icon name="plus" size={16} />
          <span>{t("users.add")}</span>
        </button>
      </div>

      {creating && (
        <CreateUser
          roles={roles}
          onDone={() => {
            setCreating(false);
            void load();
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      {firstLoad ? (
        <SkeletonRows
          rows={5}
          columns={[2, 3, 2, 2, 1, 1]}
          label={t("users.title")}
        />
      ) : (
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>{t("users.name")}</th>
              <th>{t("users.email")}</th>
              <th>{t("users.role")}</th>
              <th>{t("users.lastSignIn")}</th>
              <th>{t("users.status")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <Fragment key={u.id}>
              <tr className={u.is_active ? "" : "dim"}>
                <td>{u.full_name}</td>
                <td>
                  <code>{u.email}</code>
                </td>
                <td>
                  {/* READ-ONLY here. This was a second control for the same field, with
                      opposite save semantics: it auto-saved on change, with no button and
                      no confirmation, while the editor below staged the same field behind
                      Save. Both were visible at once, and the row's version wrote the
                      LIST's values -- so changing a role here silently discarded whatever
                      was being typed in the open editor. The role is changed in one place
                      now, with everything else about the person. */}
                  <span className="role-name">
                    {roles.find((r) => r.key === (u.roles[0] ?? ""))?.name ?? (u.roles[0] ?? "—")}
                  </span>
                </td>
                <td className="num">
                  {u.last_login_at ? formatDateTime(u.last_login_at, lang) : t("users.never")}
                </td>
                <td>
                  <span className={`pill ${u.is_active ? "ok" : "off"}`}>
                    {u.is_active ? t("users.active") : t("users.deactivated")}
                  </span>
                </td>
                <td>
                  <button
                    type="button"
                    className="btn ghost small"
                    aria-expanded={editing === u.id}
                    onClick={() => setEditing(editing === u.id ? null : u.id)}
                  >
                    {editing === u.id ? t("users.close") : t("users.edit")}
                  </button>
                </td>
              </tr>
              {editing === u.id && (
                <tr className="detail-row">
                  <td colSpan={6}>
                    <UserEditor
                      user={u}
                      roles={roles}
                      permissions={permissions}
                      implications={implications}
                      onSaved={() => void load()}
                      onClose={() => setEditing(null)}
                    />
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      )}
      <p className="muted small">{t("users.revokeNote")}</p>
    </div>
  );
}

function CreateUser({
  roles,
  onDone,
  onCancel,
}: {
  roles: Role[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  // One role, defaulting to the least privileged so a mis-click cannot over-grant.
  const [picked, setPicked] = useState<string>(
    roles.find((r) => r.key === "viewer")?.key ?? roles[roles.length - 1]?.key ?? "",
  );
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await api.post("/users", { email, full_name: fullName, password, role: picked });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("users.createFailed"));
    }
  }

  return (
    <form className="card" onSubmit={submit}>
      <h3>{t("users.newTitle")}</h3>
      <div className="grid-2">
        <div>
          <label htmlFor="new-name">{t("users.fullName")}</label>
          <input id="new-name" required value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </div>
        <div>
          <label htmlFor="new-email">{t("users.email")}</label>
          <input id="new-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
      </div>

      <label htmlFor="new-pw">{t("users.tempPassword")}</label>
      <input
        id="new-pw"
        type="password"
        required
        minLength={12}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <p className="muted small">{t("users.tempPasswordHelp")}</p>

      <label htmlFor="new-role">{t("users.role")}</label>
      <Select
        id="new-role"
        value={picked}
        onChange={setPicked}
        ariaLabel={t("users.role")}
        options={roles.map((r) => ({
          value: r.key,
          label: r.name,
          description: r.description,
        }))}
      />

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div className="row-end">
        <button type="button" className="btn ghost" onClick={onCancel}>
          {t("users.cancel")}
        </button>
        <button type="submit" className="btn primary">
          {t("users.create")}
        </button>
      </div>
    </form>
  );
}
